import type { ConfettiShape, ParticleShape, Piece } from "@/interfaces/redemption";
import {
  ALL_SHAPES,
  BAG,
  CANNON_STAGGER_MS,
  DRAG,
  FADE_MS,
  FADE_START_MS,
  FOIL,
  GRAVITY,
  LAUNCH_MAX,
  LAUNCH_MIN,
  MAX_LIFE_MS,
  PER_CANNON,
  SECOND_BEAT_MS,
  SPREAD_DEG,
  TERMINAL,
  TWO_PI,
} from "../../../constants/confetti";

/**
 * Confetti canvas helpers — the pure functions that build and animate the burst.
 * Palette/tuning live in @/constants/confetti; the particle model (ParticleShape,
 * Piece) in @/interfaces/redemption. Evolved from the prototype's launchFpConfetti
 * into the two-cannon canopy.
 */

const rad = (deg: number) => (deg * Math.PI) / 180;

// Ported from the campaign-builder master prototype's drawConfettiShape so the
// builder's Branding "Shape" setting renders identically in the recipient flow.
function drawShape(ctx: CanvasRenderingContext2D, shape: ParticleShape, s: number) {
  switch (shape) {
    case "rect":
      ctx.fillRect(-s / 2, -s / 4, s, s / 2);
      break;
    case "circle":
      ctx.beginPath();
      ctx.arc(0, 0, s / 2, 0, TWO_PI);
      ctx.fill();
      break;
    case "oval":
      ctx.beginPath();
      ctx.ellipse(0, 0, s / 2, s / 3.2, 0, 0, TWO_PI);
      ctx.fill();
      break;
    case "teardrop":
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.5);
      ctx.bezierCurveTo(s * 0.35, -s * 0.2, s * 0.35, s * 0.25, 0, s * 0.5);
      ctx.bezierCurveTo(-s * 0.35, s * 0.25, -s * 0.35, -s * 0.2, 0, -s * 0.5);
      ctx.fill();
      break;
    case "heart": {
      const hs = s * 0.3;
      ctx.beginPath();
      ctx.moveTo(0, hs * 0.4);
      ctx.bezierCurveTo(0, -hs * 0.2, -hs * 1.5, -hs * 0.8, -hs * 1.5, hs * 0.05);
      ctx.bezierCurveTo(-hs * 1.5, hs * 0.9, 0, hs * 1.6, 0, hs * 2);
      ctx.bezierCurveTo(0, hs * 1.6, hs * 1.5, hs * 0.9, hs * 1.5, hs * 0.05);
      ctx.bezierCurveTo(hs * 1.5, -hs * 0.8, 0, -hs * 0.2, 0, hs * 0.4);
      ctx.fill();
      break;
    }
    case "star": {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const oa = ((i * 72 - 90) * Math.PI) / 180;
        const ia = ((i * 72 + 36 - 90) * Math.PI) / 180;
        if (i === 0) ctx.moveTo(Math.cos(oa) * s * 0.5, Math.sin(oa) * s * 0.5);
        else ctx.lineTo(Math.cos(oa) * s * 0.5, Math.sin(oa) * s * 0.5);
        ctx.lineTo(Math.cos(ia) * s * 0.2, Math.sin(ia) * s * 0.2);
      }
      ctx.closePath();
      ctx.fill();
      break;
    }
  }
}

function buildPieces(W: number, H: number, shape: ConfettiShape, colors?: string[]): Piece[] {
  // Bottom-corner cannons, aimed up and inward for a big arcing canopy.
  const cannons = [
    { x: W * 0.03, y: H + 8, aim: rad(-75), fire: 0 }, // bottom-left → up & right, fires first
    { x: W * 0.97, y: H + 8, aim: rad(-105), fire: CANNON_STAGGER_MS }, // bottom-right, staggered
  ];
  // A campaign color scheme (flat 6-color) overrides the default two-tone brand
  // FOIL: scheme pieces are single-tone (front === back); the brand palette keeps
  // its metallic front/back sheen.
  const scheme = colors?.length ? colors : null;
  const pickPair = (): readonly [string, string] => {
    if (scheme) {
      const c = scheme[(Math.random() * scheme.length) | 0] ?? scheme[0] ?? "#d4af37";
      return [c, c];
    }
    return FOIL[BAG[(Math.random() * BAG.length) | 0] ?? 0] ?? (["#ac8958", "#f3e3cd"] as const);
  };
  const pieces: Piece[] = [];
  for (let beat = 0; beat < 2; beat++) {
    const beatDelay = beat * SECOND_BEAT_MS;
    for (const c of cannons) {
      for (let i = 0; i < PER_CANNON; i++) {
        const ang = c.aim + rad((Math.random() * 2 - 1) * SPREAD_DEG);
        const spd = LAUNCH_MIN + Math.random() * (LAUNCH_MAX - LAUNCH_MIN);
        const pair = pickPair();
        const size = (6 + Math.random() * 7) * (Math.random() < 0.12 ? 1.5 : 1); // a few hero pieces
        // `mixed` → random shape per particle; otherwise every particle is the chosen shape.
        const pieceShape: ParticleShape =
          shape === "mixed"
            ? (ALL_SHAPES[(Math.random() * ALL_SHAPES.length) | 0] ?? "rect")
            : shape;
        pieces.push({
          x: c.x + (Math.random() - 0.5) * 24,
          y: c.y,
          vx: Math.cos(ang) * spd,
          vy: Math.sin(ang) * spd,
          size,
          shape: pieceShape,
          front: pair[0],
          back: pair[1],
          rot: Math.random() * TWO_PI,
          rotV: (Math.random() - 0.5) * 0.3,
          tumble: Math.random() * TWO_PI,
          tumbleV: 0.12 + Math.random() * 0.18,
          swayA: 0.4 + Math.random() * 0.9,
          phase: Math.random() * TWO_PI,
          delay: beatDelay + c.fire + Math.random() * 120,
          dead: false,
        });
      }
    }
  }
  return pieces;
}

/** Runs the burst; returns a canceller so the caller can stop the rAF loop
 *  (on unmount mid-burst, or before a re-fire). */
export function launchConfetti(
  canvas: HTMLCanvasElement,
  shape: ConfettiShape,
  colors?: string[],
): () => void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = window.innerWidth;
  const H = window.innerHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  ctx.scale(dpr, dpr);

  const pieces = buildPieces(W, H, shape, colors);
  // A chosen shape stays face-up so its silhouette reads; only the brand "mixed"
  // default tumbles edge-on for the foil sheen (which flattens — and so erases — a
  // specific shape, making every shape look identical).
  const faceUp = shape !== "mixed";
  let last = performance.now();
  const start = last;
  let rafId = 0;

  const tick = (now: number) => {
    const dt = Math.min((now - last) / 16.667, 3);
    last = now;
    const elapsed = now - start;
    const fade = elapsed > FADE_START_MS ? Math.max(0, 1 - (elapsed - FADE_START_MS) / FADE_MS) : 1;
    ctx.clearRect(0, 0, W, H);
    let alive = false;
    for (const p of pieces) {
      if (p.dead) continue;
      if (elapsed < p.delay) {
        alive = true; // parked at the cannon mouth until its beat fires
        continue;
      }
      p.vy = Math.min(p.vy + GRAVITY * dt, TERMINAL); // gravity up, terminal cap = flutter down
      p.vx *= DRAG ** dt;
      p.x += p.vx * dt + Math.sin(elapsed / 220 + p.phase) * p.swayA * dt; // horizontal sway
      p.y += p.vy * dt;
      p.rot += p.rotV * dt;
      p.tumble += p.tumbleV * dt;
      if (p.y > H + 40) {
        p.dead = true; // fell out of frame — exit, don't vanish
        continue;
      }
      alive = true;
      const flip = Math.cos(p.tumble);
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      // 3D tumble flips pieces edge-on like foil; skip the squish for a chosen shape so
      // its outline stays visible (the sheen still alternates front/back via the flip sign).
      if (!faceUp) ctx.scale(1, Math.max(0.12, Math.abs(flip)));
      ctx.fillStyle = flip >= 0 ? p.front : p.back; // front / back face = sheen
      drawShape(ctx, p.shape, p.size);
      ctx.restore();
    }
    if (alive && elapsed < MAX_LIFE_MS) rafId = requestAnimationFrame(tick);
    else ctx.clearRect(0, 0, W, H);
  };
  rafId = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(rafId);
}
